import { useEffect, useRef, useState } from "react";

/**
 * Measures the map area so the shared `MapView` can fill it — MapView renders at
 * a fixed pixel height, so the page has to hand it one.
 *
 * Attach the returned ref to the sizing element and pass `height` to MapView;
 * it stays 0 until the first measurement, which is the page's cue not to mount
 * the map yet.
 */
export const useMapAreaHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only react to meaningful changes — avoids a sub-pixel ResizeObserver
    // oscillation churning the map.
    const update = () =>
      setHeight((prev) => (Math.abs(el.clientHeight - prev) > 2 ? el.clientHeight : prev));
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  return { ref, height };
};
